import { Logger } from '@core/model/domain/logger';
import {
  BehaviorSubject,
  catchError,
  defer,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  timeout,
  timer,
} from 'rxjs';

const CONVERT_SECONDS_TO_MS = 1000;
interface PollCmd<TApiArgs, TApiResponse> {
  intervalInSeconds: number;
  timeoutInSeconds: number;
  /*
    Wait this long before the FIRST call. Useful when the condition cannot
    possibly be met right away (a user is still authenticating elsewhere):
    the calls that would surely come back empty are skipped. Counted within
    the timeout. Defaults to no delay.
  */
  initialDelayInSeconds?: number;
  apiCall: (arg?: TApiArgs) => Observable<TApiResponse>;
  shouldContinuePolling: (arg: TApiResponse) => boolean;
  manualForceStop: BehaviorSubject<boolean>;
  logger?: Logger;
  pollingName: 'NEW_BANK_CONNECTION' | 'BANK_ACCOUNT_ANALYSIS_COMPLETION' | 'CASE_STATE_UPDATE';
}

export function poll<TApiArgs, TApiResponse>(
  input: PollCmd<TApiArgs, TApiResponse>
): Observable<TApiResponse> {
  input.logger?.logEvent(`Polling: started - ${input.pollingName}`);

  const initialDelayInMs = (input.initialDelayInSeconds ?? 0) * CONVERT_SECONDS_TO_MS;

  /*
    The retry loop below re-subscribes to its source on every attempt, so the
    initial delay must sit OUTSIDE of it: applied once, before the first call,
    never again between calls (the interval takes over from there).
    `defer` keeps apiCall from being invoked before the delay elapsed — the
    call is only made on subscription, i.e. once the timer fired.
  */
  const pollingLoop$ = defer(() => input.apiCall()).pipe(
    switchMap((apiResponse) => {
      if (input.shouldContinuePolling(apiResponse)) {
        return throwError(() => 'CONDITION_NOT_REACHED');
      }

      input.logger?.logEvent(`Polling: finished - ${input.pollingName}`);
      return of(apiResponse);
    }),
    retry({
      delay: () => {
        if (input.manualForceStop.value === true) {
          return throwError(() => 'STOPPED_POLLING_VIA_USER_ACTION');
        }

        return timer(input.intervalInSeconds * CONVERT_SECONDS_TO_MS);
      },
    })
  );

  return timer(initialDelayInMs).pipe(
    switchMap(() => pollingLoop$),
    timeout({ first: input.timeoutInSeconds * CONVERT_SECONDS_TO_MS }),
    catchError((err) => {
      /*
        Note: in case of STOPPED_POLLING_VIA_USER_ACTION the log
        may have a delay equal to the interval
      */
      if (err === 'STOPPED_POLLING_VIA_USER_ACTION') {
        input.logger?.logEvent(`Polling: stopped polling via user action - ${input.pollingName}`);
      } else if (err.name === 'TimeoutError') {
        input.logger?.logEvent(`Polling: timeout - ${input.pollingName}`);
      }

      throw err;
    })
  );
}
